import React from 'react';
import { PnlLevel } from '../pnl/api';
import { fetchLHCData, fetchLhcDropdowns, fetchLhcRouteDetails, fetchLhcRouteVendors, suggestRouteGeography, fetchRouteVehicleTypes, LhcDropdownLevel, LhcRecord, LhcRouteDetailsPayload, LhcRouteDetailsResponse, LhcStats, LhcSummaryRecord, RouteGeoSuggestion, RouteLevel, fetchLhcYears, updateLhcDropdowns, VendorRecord, VendorStats } from './api';
import Dropdowns from './components/Dropdowns';
import { useBreadcrumbs } from 'contexts/BreadcrumbsContext';
import LhcTable from './components/LhcTable';
import { Button } from '../../components/ui/button';
import { Combobox } from '../../components/ui/combobox';
import { ArrowLeft } from 'lucide-react';
import LhcRouteGraph, { OUTLIERS_COLOR_BY } from './components/LhcRouteGraph';
import RouteSearchPanel from './components/RouteSearchPanel';
import LhcStatCards from './components/LhcStatCards';
import VendorStatCards, { VendorStatFilter } from './components/VendorStatCards';

type RouteByOption = 'State' | 'District' | 'City' | 'Location';

const COLOR_BY_OPTIONS = [
    { value: 'vendor_type', label: 'Vendor Type' },
    { value: 'va_category', label: 'VA Category' },
    { value: 'lhc_category', label: 'LHC Category' },
    { value: 'branch_name', label: 'Branch' },
    { value: OUTLIERS_COLOR_BY, label: 'Outliers' },
];

function useStableRef<T>(initial: T) {
    const ref = React.useRef(initial);
    return ref;
}

function toIsoWithoutOffset(date?: Date | null): string | null {
    if (!date) return null;
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    return d.toISOString();
}

const LhcAnalysisPage: React.FC = () => {
    const { setBreadcrumbs } = useBreadcrumbs();
    const didInitRef = useStableRef(false);

    // --- Sorting ---
    const [sortBy, setSortBy] = React.useState<string>('count');
    const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('desc');

    // --- P&L Dropdowns ---
    const [years, setYears] = React.useState<string[]>([]);
    const [year, setYear] = React.useState<string>('');
    const [levels, setLevels] = React.useState<PnlLevel[]>([]);
    const [levelsReady, setLevelsReady] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string>('');

    // --- Table ---
    const [tableLoading, setTableLoading] = React.useState<boolean>(false);
    const [tableError, setTableError] = React.useState<string>('');
    const [tableData, setTableData] = React.useState<LhcRecord[]>([]);

    const [hasLoadedOnce, setHasLoadedOnce] = React.useState<boolean>(false);

    // --- Pagination ---
    const [currentPage, setCurrentPage] = React.useState<number>(1);
    const [rowsPerPage, setRowsPerPage] = React.useState<number>(20);
    const [totalRows, setTotalRows] = React.useState<number>(0);
    const [totalPages, setTotalPages] = React.useState<number>(1);

    // --- Filters ---
    const [routeBy, setRouteBy] = React.useState<RouteByOption | ''>('City');
    const [dateFrom, setDateFrom] = React.useState<Date | undefined>(undefined);
    const [dateTo, setDateTo] = React.useState<Date | undefined>(undefined);
    const [lhcDropdownLevels, setLhcDropdownLevels] = React.useState<LhcDropdownLevel[]>([]);

    // --- Details ---
    const [stats, setStats] = React.useState<LhcStats | null>(null);
    const [selectedRowSummary, setSelectedRowSummary] = React.useState<LhcSummaryRecord | null>(null);
    const [detailsLoading, setDetailsLoading] = React.useState<boolean>(false);

    // --- View Mode ---
    const [showingDetails, setShowingDetails] = React.useState<boolean>(false);
    const [viewMode, setViewMode] = React.useState<'table' | 'graph' | 'vendors'>('table');
    const [colorBy, setColorBy] = React.useState<string>('vendor_type');

    const [vendorData, setVendorData] = React.useState<VendorRecord[]>([]);
    const [vendorStats, setVendorStats] = React.useState<VendorStats | null>(null);
    const [vendorTotalCount, setVendorTotalCount] = React.useState<number>(0);
    const [activeVendorStat, setActiveVendorStat] = React.useState<VendorStatFilter>('Total');

    const [previousTableData, setPreviousTableData] = React.useState<LhcRecord[]>([]);
    const [previousPagination, setPreviousPagination] = React.useState({
        page: 1,
        total: 0,
        totalPages: 1,
    });
    const [previousSort, setPreviousSort] = React.useState({
        sortBy: '',
        sortDir: 'asc' as 'asc' | 'desc',
    });
    const [detailsPayload, setDetailsPayload] = React.useState<LhcRouteDetailsPayload | null>(null);
    const [detailsPayloadOriginal, setDetailsPayloadOriginal] = React.useState<LhcRouteDetailsPayload | null>(null);
    const [routeDetailsResponse, setRouteDetailsResponse] = React.useState<LhcRouteDetailsResponse | null>(null);
    const [activeStat, setActiveStat] = React.useState<'Min' | 'Max' | 'Avg' | 'Outliers' | null>(null);

    // --- Dedupe API Calls ---
    const lastSummaryPayloadRef = React.useRef<string>('');
    const lastDetailsPayloadRef = React.useRef<string>('');
    const lastVendorPayloadRef = React.useRef<string>('');

    const buildFilters = React.useCallback(() => {
        const filters: Record<string, string[]> = {};

        levels.forEach(level => {
            if (level.selected?.length) {
                filters[String(level.id)] = level.selected;
            }
        });
        return filters;
    }, [levels]);

    const buildLhcDropdownPayload = React.useCallback(() => {
        return lhcDropdownLevels.map(l => ({
            id: l.id,
            value: l.selected?.[0] ?? null,
        }));
    }, [lhcDropdownLevels]);

    const buildRouteContextPayload = React.useCallback(
        (base: LhcRouteDetailsPayload) => ({
            origin: base.origin,
            destination: base.destination,
            route_by: base.route_by,
            origin_type: base.origin_type,
            destination_type: base.destination_type,
            vehicle_type: base.vehicle_type,
            year,
            filters: buildFilters(),
            dateFrom: toIsoWithoutOffset(dateFrom),
            dateTo: toIsoWithoutOffset(dateTo),
            lhcDropdownLevels: buildLhcDropdownPayload(),
            page: currentPage,
            pageSize: rowsPerPage,
            sort: {
                by: sortBy,
                dir: sortDir,
            },
        }),
        [year, buildFilters, dateFrom, dateTo, buildLhcDropdownPayload, currentPage, rowsPerPage, sortBy, sortDir]
    );

    const fetchLevels = React.useCallback(async (selectionMap?: Record<string, string[]>) => {
        if (!year) return;

        setLoading(true);
        setError('');
        setLevelsReady(false);

        try {
            const data = await updateLhcDropdowns({
                year,
                selection: selectionMap || {},
            });

            setLevels(Array.isArray(data?.levels) ? data.levels : []);
        } catch (err: any) {
            const message = err instanceof Error ? err.message : 'Something went wrong';
            setError(message);
            setLevels([]);
        } finally {
            setLevelsReady(true);
            setLoading(false);
        }
    }, [year]);

    const fetchLhcLevels = React.useCallback(async () => {
        try {
            const data = await fetchLhcDropdowns();
            setLhcDropdownLevels(Array.isArray(data?.levels) ? data.levels : []);
        } catch (err) {
            console.error('Failed to fetch LHC dropdowns:', err);
            setLhcDropdownLevels([]);
        }
    }, []);

    const summaryPayload = React.useMemo(() => {
        return {
            year,
            filters: buildFilters(),
            routeBy: routeBy?.toLowerCase() || '',
            dateFrom: toIsoWithoutOffset(dateFrom),
            dateTo: toIsoWithoutOffset(dateTo),
            lhcDropdownLevels: buildLhcDropdownPayload(),
            page: currentPage,
            pageSize: rowsPerPage,
            sort: {
                by: sortBy,
                dir: sortDir,
            },
        };
    }, [year, buildFilters, routeBy, dateFrom, dateTo, buildLhcDropdownPayload, currentPage, rowsPerPage, sortBy, sortDir]);

    const detailsFetchPayload = React.useMemo(() => {
        if (!detailsPayload) return null;

        return {
            ...detailsPayload,
            year,
            filters: buildFilters(),
            dateFrom: toIsoWithoutOffset(dateFrom),
            dateTo: toIsoWithoutOffset(dateTo),
            lhcDropdownLevels: buildLhcDropdownPayload(),
            route_by: routeBy?.toLowerCase() || 'state',
            page: currentPage,
            pageSize: rowsPerPage,
            sort: {
                by: sortBy,
                dir: sortDir,
            },
            viewMode: viewMode === 'vendors' ? 'table' : viewMode,
        };
    }, [detailsPayload, year, buildFilters, routeBy, dateFrom, dateTo, buildLhcDropdownPayload, currentPage, rowsPerPage, sortBy, sortDir, viewMode ]);

    const vendorFetchPayload = React.useMemo(() => {
        if (!detailsPayload) return null;
        return {
            ...buildRouteContextPayload(detailsPayload),
            ...(activeVendorStat !== 'Total' ? { vendor_type: activeVendorStat } : {}),
        };
    }, [detailsPayload, buildRouteContextPayload, activeVendorStat]);

    const vendorTableData = React.useMemo(
        () =>
            vendorData.map(v => ({
                vendor_name: v.vendor_name,
                vendor_type: v.vendor_type,
                lhc_count: v.lhc_count,
                total_value: v.total_value,
                avg_value: v.avg_value,
                min_value: v.min_value,
                max_value: v.max_value,
                concentration_percent: v.concentration_percent,
            })),
        [vendorData]
    );

    const buildSelectionFromLevels = React.useCallback((currentLevels: PnlLevel[]) => {
        const map: Record<string, string[]> = {};

        for (const level of currentLevels) {
            if (level?.selected?.length) {
                map[String(level.id)] = level.selected;
            }
        }

        return map;
    }, []);

    const handleBackToSummary = React.useCallback(() => {
        lastDetailsPayloadRef.current = '';

        setTableData(previousTableData);
        setShowingDetails(false);
        setCurrentPage(previousPagination.page);
        setTotalRows(previousPagination.total);
        setTotalPages(previousPagination.totalPages);
        setSortBy(previousSort.sortBy);
        setSortDir(previousSort.sortDir);
        setDetailsPayload(null);

        setStats(null);
        setSelectedRowSummary(null);
        setActiveStat(null);
        setViewMode('table');
        setVendorData([]);
        setVendorStats(null);
        setVendorTotalCount(0);
        setActiveVendorStat('Total');
        lastVendorPayloadRef.current = '';

        setBreadcrumbs([
            { label: 'Reports' },
            { label: 'LHC Analysis' },
        ]);
    }, [
        previousTableData,
        previousPagination,
        previousSort,
        setBreadcrumbs,
    ]);

    // Shared drill-down: opens the detail view for a specific route + vehicle
    // type. Used by both Top Routes row clicks and the Search Route panel.
    // origin/destination can be matched at different levels (flexible routes).
    const openRouteDetails = async (opts: {
        origin: string;
        destination: string;
        originType: string;
        destinationType: string;
        vehicleType: string;
        totalCount?: number;
    }) => {
        const originType = (opts.originType || 'state').toLowerCase() as RouteLevel;
        const destinationType = (opts.destinationType || 'state').toLowerCase() as RouteLevel;
        try {
            setDetailsLoading(true);
            setTableError('');

            const selectedSummary: LhcSummaryRecord = {
                origin: opts.origin,
                destination: opts.destination,
                vehicle_type: opts.vehicleType,
                total_count: opts.totalCount ?? 0,
                min_value: 0,
                max_value: 0,
                avg_value: 0,
            };

            const payload: LhcRouteDetailsPayload = {
                origin: opts.origin,
                destination: opts.destination,
                route_by: originType,
                origin_type: originType,
                destination_type: destinationType,
                vehicle_type: opts.vehicleType,
                year,
                filters: buildFilters(),
                dateFrom: toIsoWithoutOffset(dateFrom),
                dateTo: toIsoWithoutOffset(dateTo),
                lhcDropdownLevels: buildLhcDropdownPayload(),
                page: 1,
                pageSize: rowsPerPage,
                sort: viewMode === 'vendors' ? { by: 'concentration_percent', dir: 'desc' } : { by: 'lhc_date', dir: sortDir },
                viewMode: viewMode === 'vendors' ? 'table' : viewMode,
            };

            if (viewMode === 'vendors') {
                const { viewMode: _, lorry_hire_expenses, outliers, ...vendorPayload } = payload;
                const res = await fetchLhcRouteVendors(vendorPayload);

                setSelectedRowSummary(selectedSummary);
                if (!showingDetails) {
                    setPreviousTableData(tableData);
                    setPreviousPagination({
                        page: currentPage,
                        total: totalRows,
                        totalPages,
                    });
                    setPreviousSort({ sortBy, sortDir });
                }

                setVendorData(res.data || []);
                setVendorStats(res.stats || null);
                setVendorTotalCount(res.pagination?.total || 0);
                setTotalRows(res.pagination?.total || 0);
                setTotalPages(res.pagination?.totalPages || 1);
                setSortBy('concentration_percent');
                setSortDir('desc');
                setDetailsPayload(payload);
                setDetailsPayloadOriginal(payload);
                setShowingDetails(true);
                setCurrentPage(1);
                setActiveStat(null);
                setActiveVendorStat('Total');
                lastVendorPayloadRef.current = JSON.stringify(vendorPayload);

                setBreadcrumbs([
                    { label: 'Reports' },
                    {
                        label: 'LHC Analysis',
                        onClick: handleBackToSummary,
                    },
                    { label: `${opts.origin} → ${opts.destination} | ${opts.vehicleType}` },
                ]);
                return;
            }

            const res = await fetchLhcRouteDetails(payload);

            setSelectedRowSummary(selectedSummary);

            // Only snapshot the discovery (Top Routes) view on the first drill-in.
            // Searching a new route from within a detail view must not overwrite it,
            // so "Back" always returns to the original Top Routes list.
            if (!showingDetails) {
                setPreviousTableData(tableData);
                setPreviousPagination({
                    page: currentPage,
                    total: totalRows,
                    totalPages,
                });
                setPreviousSort({ sortBy, sortDir });
            }

            setRouteDetailsResponse(res);
            setDetailsPayloadOriginal(payload);

            setTableData(res.data || []);
            setTotalRows(res.pagination?.total || 0);
            setTotalPages(res.pagination?.totalPages || 1);

            setStats({
                origin: selectedSummary.origin,
                destination: selectedSummary.destination,
                vehicle_type: selectedSummary.vehicle_type,
                total_count: res.pagination?.total ?? 0,
                min_count: res.stats?.min_count ?? 0,
                max_count: res.stats?.max_count ?? 0,
                avg_count: res.stats?.avg_count ?? 0,
                min_value: res.stats?.min_value ?? 0,
                max_value: res.stats?.max_value ?? 0,
                avg_value: res.stats?.avg_value ?? 0,
                outlier_count: res.stats?.outlier_count ?? 0,
            });

            setSortBy('lhc_date');
            setCurrentPage(1);
            setActiveStat(null); // new route opens showing all (Total highlighted)
            setDetailsPayload(payload);
            setShowingDetails(true);

            lastDetailsPayloadRef.current = JSON.stringify(payload);

            setBreadcrumbs([
                { label: 'Reports' },
                {
                    label: 'LHC Analysis',
                    onClick: handleBackToSummary,
                },
                { label: `${opts.origin} → ${opts.destination} | ${opts.vehicleType}` },
            ]);
        } catch (err) {
            console.error('❌ Error fetching LHC route details:', err);
            setTableError('Failed to fetch route details.');
        } finally {
            setDetailsLoading(false);
        }
    };

    const handleRowClick = (row: Record<string, any>) =>
        openRouteDetails({
            origin: row.origin,
            destination: row.destination,
            originType: routeBy || 'state',
            destinationType: routeBy || 'state',
            vehicleType: row.vehicle_type,
            totalCount: row.count ?? 0,
        });

    // --- Route Search panel handlers (scoped to the active filters) ---
    const handleSuggestRoute = React.useCallback(
        async (side: 'origin' | 'destination', q: string): Promise<RouteGeoSuggestion[]> => {
            const res = await suggestRouteGeography({
                side,
                q,
                year,
                filters: buildFilters(),
                dateFrom: toIsoWithoutOffset(dateFrom),
                dateTo: toIsoWithoutOffset(dateTo),
                lhcDropdownLevels: buildLhcDropdownPayload(),
            });
            return res.suggestions || [];
        },
        [year, buildFilters, dateFrom, dateTo, buildLhcDropdownPayload]
    );

    const handleFetchVehicleTypes = React.useCallback(
        async (origin: RouteGeoSuggestion, destination: RouteGeoSuggestion): Promise<string[]> => {
            const res = await fetchRouteVehicleTypes({
                origin: origin.value,
                destination: destination.value,
                origin_type: origin.level,
                destination_type: destination.level,
                year,
                filters: buildFilters(),
                dateFrom: toIsoWithoutOffset(dateFrom),
                dateTo: toIsoWithoutOffset(dateTo),
                lhcDropdownLevels: buildLhcDropdownPayload(),
            });
            return res.vehicle_types || [];
        },
        [year, buildFilters, dateFrom, dateTo, buildLhcDropdownPayload]
    );

    const handleStatCardClick = async (label: 'Min' | 'Max' | 'Avg' | 'Outliers' | 'Total', value: number) => {
        // "Total Count" clears any active filter and shows ALL LHCs / graph points.
        if (label === 'Total') {
            if (activeStat === null) return; // already showing everything
            setActiveStat(null);
            setCurrentPage(1);
            setDetailsPayload(detailsPayloadOriginal);
            lastDetailsPayloadRef.current = ''; // force the details-fetch effect to reload full data
            return;
        }

        if (activeStat === label) {
            setActiveStat(null);

            if (routeDetailsResponse) {
                setTableData(routeDetailsResponse.data || []);
                setTotalRows(routeDetailsResponse.pagination?.total || 0);
                setTotalPages(routeDetailsResponse.pagination?.totalPages || 1);

                setDetailsPayload(detailsPayloadOriginal);
                setShowingDetails(true);
                setCurrentPage(detailsPayloadOriginal?.page || 1);

                if (detailsPayloadOriginal) {
                    lastDetailsPayloadRef.current = JSON.stringify(detailsPayloadOriginal);
                }
            } else {
                setTableData(previousTableData);
                setTotalRows(previousPagination.total);
                setTotalPages(previousPagination.totalPages);
                setShowingDetails(false);
                setCurrentPage(previousPagination.page);
            }
            return;
        }

        setActiveStat(label);
        if (!stats) return;

        const payload: LhcRouteDetailsPayload = {
            origin: stats.origin,
            destination: stats.destination,
            route_by: detailsPayloadOriginal?.route_by ?? (routeBy?.toLowerCase() || 'state'),
            origin_type: detailsPayloadOriginal?.origin_type,
            destination_type: detailsPayloadOriginal?.destination_type,
            vehicle_type: stats.vehicle_type,
            // Outliers filter to the ±10% band; Min/Max/Avg filter to an exact value.
            ...(label === 'Outliers' ? { outliers: true } : { lorry_hire_expenses: value }),
            year,
            filters: buildFilters(),
            dateFrom: toIsoWithoutOffset(dateFrom),
            dateTo: toIsoWithoutOffset(dateTo),
            lhcDropdownLevels: buildLhcDropdownPayload(),
            page: 1,
            pageSize: rowsPerPage,
            sort: { by: sortBy, dir: sortDir },
            viewMode,
        };

        try {
            setDetailsLoading(true);
            setTableError('');
            const res = await fetchLhcRouteDetails(payload);

            setTableData(res.data || []);
            setTotalRows(res.pagination?.total || 0);
            setTotalPages(res.pagination?.totalPages || 1);

            setDetailsPayload(payload);
            setShowingDetails(true);
            setCurrentPage(1);

            lastDetailsPayloadRef.current = JSON.stringify(payload);
        } catch (err) {
            console.error(`❌ Error fetching ${label} details:`, err);
            setTableError('Failed to fetch route details.');
        } finally {
            setDetailsLoading(false);
        }
    };

    const handleViewModeChange = React.useCallback((mode: 'table' | 'graph' | 'vendors') => {
        if (mode === viewMode) return;
        setViewMode(mode);
        setCurrentPage(1);
        lastDetailsPayloadRef.current = '';
        lastVendorPayloadRef.current = '';
        if (mode === 'vendors') {
            setSortBy('concentration_percent');
            setSortDir('desc');
            setActiveVendorStat('Total');
        } else if (mode === 'table') {
            setSortBy('lhc_date');
        }
    }, [viewMode]);

    const handleVendorStatCardClick = React.useCallback((stat: VendorStatFilter) => {
        if (stat === activeVendorStat) return;
        setActiveVendorStat(stat);
        setCurrentPage(1);
        lastVendorPayloadRef.current = '';
    }, [activeVendorStat]);

    const handleSort = React.useCallback((column: string) => {
        setCurrentPage(1);
        if (sortBy === column) {
            setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortBy(column);
            setSortDir('asc');
        }
    }, [sortBy]);

    const wrapFilterChange = React.useCallback(
        (setter: React.Dispatch<any>) => (value: any) => {
            setter(value);
            setCurrentPage(1);
            lastSummaryPayloadRef.current = '';
            lastDetailsPayloadRef.current = '';
            lastVendorPayloadRef.current = '';
        },
        []
    );

    const handleSelectChange = React.useCallback((levelId: number | string, value: string[]) => {
        const desc = levels.filter(l => l?.visible).sort((a, b) => Number(b.id) - Number(a.id));
        const indexById = Object.fromEntries(desc.map((l, i) => [String(l.id), i]));
        const changedIdx = indexById[String(levelId)];

        const nextLevels = levels.map(lvl => {
            const idx = indexById[String(lvl.id)];
            if (idx < changedIdx) return lvl.id === levelId ? { ...lvl, selected: value.length ? value : null } : lvl;
            if (idx === changedIdx) return { ...lvl, selected: value.length ? value : null };
            return { ...lvl, selected: null };
        });

        setLevels(nextLevels);
        const selection = buildSelectionFromLevels(nextLevels);
        if (!value.length) delete selection[String(levelId)];
        setCurrentPage(1);
        lastSummaryPayloadRef.current = '';
        lastDetailsPayloadRef.current = '';
        lastVendorPayloadRef.current = '';

        fetchLevels(selection);
    }, [levels, buildSelectionFromLevels, fetchLevels]);

    const handleYearChange = React.useCallback((newYear: string) => {
        setLevels([]);
        setLhcDropdownLevels(prev =>
            prev.map(l => ({ ...l, selected: [] }))
        );
        setYear(newYear);
        setCurrentPage(1);
        setShowingDetails(false);
        setDetailsPayload(null);
        setStats(null);
        setSelectedRowSummary(null);
        setActiveStat(null);
        setHasLoadedOnce(false);

        lastSummaryPayloadRef.current = '';
        lastDetailsPayloadRef.current = '';
        lastVendorPayloadRef.current = '';
    }, []);

    const handleRouteByChange = wrapFilterChange((val: string) => setRouteBy(val as RouteByOption | ''));
    const handleDateFromChange = wrapFilterChange(setDateFrom);
    const handleDateToChange = wrapFilterChange(setDateTo);
    const handleLhcLevelChange = React.useCallback((levelId: number, val: string) => {
        setLhcDropdownLevels(prev => prev.map(l => l.id === levelId ? { ...l, selected: val ? [val] : [] } : l));
        setCurrentPage(1);
        lastSummaryPayloadRef.current = '';
        lastDetailsPayloadRef.current = '';
        lastVendorPayloadRef.current = '';
    }, []);

    // --- Initialization ---
    React.useEffect(() => {
        setBreadcrumbs([{ label: 'Reports', href: '/reports/lhc-analysis' }, { label: 'LHC Analysis' }]);
        if (didInitRef.current) return;
        didInitRef.current = true;

        (async () => {
            try {
                const resp = await fetchLhcYears();
                const yrList = Array.isArray(resp?.years) ? resp.years : [];
                setYears(yrList);
                if (yrList.length > 0) setYear(yrList[0]);
            } catch {
                setYears([]);
                setYear('');
            }
            await fetchLhcLevels();
        })();
    }, [setBreadcrumbs, fetchLhcLevels, didInitRef]);

    // --- Fetch P&L Levels When Year Changes ---
    React.useEffect(() => {
        if (!year) return;

        lastSummaryPayloadRef.current = '';
        lastDetailsPayloadRef.current = '';
        setLevels([]);
        fetchLevels({});
    }, [year, fetchLevels]);

    // --- Summary Fetch ---
    React.useEffect(() => {
        if (!year || !levelsReady || showingDetails || !levels.length) return;

        const payloadKey = JSON.stringify(summaryPayload);

        if (lastSummaryPayloadRef.current === payloadKey) return;
        lastSummaryPayloadRef.current = payloadKey;

        let cancelled = false;

        const loadSummaryData = async () => {
            try {
                setTableLoading(true);
                setTableError('');

                const response = await fetchLHCData(summaryPayload);

                if (cancelled) return;

                setHasLoadedOnce(true);
                setTableData(Array.isArray(response.data) ? response.data : []);
                setTotalRows(response.pagination?.total || 0);
                setTotalPages(response.pagination?.totalPages || 1);
            } catch (err) {
                if (cancelled) return;

                setHasLoadedOnce(true);
                console.error('❌ Error fetching LHC Data:', err);
                setTableError('Failed to fetch data.');
                setTableData([]);
                setTotalRows(0);
                setTotalPages(1);
            } finally {
                if (!cancelled) {
                    setTableLoading(false);
                }
            }
        };

        loadSummaryData();
        return () => {
            cancelled = true;
        };
    }, [year, levelsReady, showingDetails, summaryPayload]);

    // --- Details Fetch ---
    React.useEffect(() => {
        if (!year || !levelsReady || !showingDetails || !detailsFetchPayload || viewMode === 'vendors') return;

        const payloadKey = JSON.stringify(detailsFetchPayload);

        if (lastDetailsPayloadRef.current === payloadKey) return;
        lastDetailsPayloadRef.current = payloadKey;

        let cancelled = false;

        const loadDetailsData = async () => {
            try {
                setDetailsLoading(true);
                setTableError('');

                const res = await fetchLhcRouteDetails(detailsFetchPayload);

                if (cancelled) return;

                setTableData(Array.isArray(res.data) ? res.data : []);
                setTotalRows(res.pagination?.total || 0);
                setTotalPages(res.pagination?.totalPages || 1);

                // Stat cards always reflect the FULL route. When a stat filter
                // (Min/Max/Avg/Outliers) is active the backend computes stats over the
                // filtered subset, so skip the stats update and keep the full-route values.
                const isFiltered =
                    detailsFetchPayload.lorry_hire_expenses != null || !!detailsFetchPayload.outliers;

                if (res.stats && !isFiltered) {
                    setStats((prev: any) => ({
                        ...prev,
                        min_count: res.stats?.min_count ?? prev?.min_count ?? 0,
                        max_count: res.stats?.max_count ?? prev?.max_count ?? 0,
                        avg_count: res.stats?.avg_count ?? prev?.avg_count ?? 0,
                        min_value: res.stats?.min_value ?? 0,
                        max_value: res.stats?.max_value ?? 0,
                        avg_value: res.stats?.avg_value ?? 0,
                        outlier_count: res.stats?.outlier_count ?? prev?.outlier_count ?? 0,
                        total_count: res.pagination?.total ?? prev?.total_count ?? 0,
                    }));
                }
            } catch (err) {
                if (cancelled) return;

                console.error('❌ Error fetching LHC route details:', err);
                setTableError('Failed to fetch route details.');
            } finally {
                if (!cancelled) {
                    setDetailsLoading(false);
                }
            }
        };

        loadDetailsData();
        return () => {
            cancelled = true;
        };
    }, [year, levelsReady, showingDetails, detailsFetchPayload, viewMode]);

    // --- Vendor Fetch ---
    React.useEffect(() => {
        if (!year || !levelsReady || !showingDetails || viewMode !== 'vendors' || !vendorFetchPayload) return;

        const payloadKey = JSON.stringify(vendorFetchPayload);

        if (lastVendorPayloadRef.current === payloadKey) return;
        lastVendorPayloadRef.current = payloadKey;

        let cancelled = false;

        const loadVendorData = async () => {
            try {
                setDetailsLoading(true);
                setTableError('');

                const res = await fetchLhcRouteVendors(vendorFetchPayload);

                if (cancelled) return;

                setVendorData(Array.isArray(res.data) ? res.data : []);
                setVendorStats(res.stats || null);
                setTotalRows(res.pagination?.total || 0);
                setTotalPages(res.pagination?.totalPages || 1);
                if (activeVendorStat === 'Total') {
                    setVendorTotalCount(res.pagination?.total || 0);
                }
            } catch (err) {
                if (cancelled) return;

                console.error('❌ Error fetching LHC route vendors:', err);
                setTableError('Failed to fetch vendor data.');
            } finally {
                if (!cancelled) {
                    setDetailsLoading(false);
                }
            }
        };

        loadVendorData();
        return () => {
            cancelled = true;
        };
    }, [year, levelsReady, showingDetails, viewMode, vendorFetchPayload, activeVendorStat]);

    return (
       <div className="space-y-6 mx-auto w-full max-w-screen-2xl 2xl:max-w-[2000px] px-4 sm:px-6">
            <div className="space-y-4">
                <Dropdowns
                    year={year}
                    years={years}
                    levels={levels}
                    loading={loading}
                    onSelectChange={handleSelectChange}
                    onYearChange={handleYearChange}
                    dateFrom={dateFrom}
                    onDateFromChange={handleDateFromChange}
                    dateTo={dateTo}
                    onDateToChange={handleDateToChange}
                    lhcDropdownLevels={lhcDropdownLevels}
                    onLhcLevelChange={handleLhcLevelChange}
                />

                <RouteSearchPanel
                    onSuggest={handleSuggestRoute}
                    onFetchVehicleTypes={handleFetchVehicleTypes}
                    onOpenAnalysis={({ origin, destination, vehicleType }) =>
                        openRouteDetails({
                            origin: origin.value,
                            destination: destination.value,
                            originType: origin.level,
                            destinationType: destination.level,
                            vehicleType,
                        })
                    }
                    loading={detailsLoading}
                />
            </div>

            {!showingDetails && (
                <div className="flex items-center justify-between gap-4">
                    <h3 className="text-base font-semibold text-slate-800">Top Routes</h3>
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-600 whitespace-nowrap">Route by</span>
                        <Combobox
                            options={(['State', 'District', 'City', 'Location'] as const).map(o => ({ value: o, label: o }))}
                            value={routeBy}
                            onValueChange={(val) => val && handleRouteByChange(val)}
                            placeholder="Route by..."
                            buttonClassName="h-9 w-[150px] px-2.5 text-sm"
                        />
                    </div>
                </div>
            )}

            {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-red-600 text-sm">{error}</p>
                </div>
            )}

            {tableError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-red-600 text-sm">{tableError}</p>
                </div>
            )}

            {showingDetails && (
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleBackToSummary}
                            className="flex items-center gap-2"
                        >
                            <ArrowLeft className="h-4 w-4" />
                        </Button>

                        {selectedRowSummary && (
                            <h3 className="text-md font-semibold">
                                LHC Details of {selectedRowSummary.origin} → {selectedRowSummary.destination} | {selectedRowSummary.vehicle_type}
                            </h3>
                        )}
                    </div>

                    <div className="flex items-center gap-3">
                        {viewMode === 'graph' && (
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-slate-600 whitespace-nowrap">Color by</span>
                                <Combobox
                                    options={COLOR_BY_OPTIONS}
                                    value={colorBy}
                                    onValueChange={(val) => val && setColorBy(val)}
                                    placeholder="Color by..."
                                    buttonClassName="h-9 w-[150px] px-2.5 text-sm"
                                />
                            </div>
                        )}

                        <div className="flex items-center space-x-2">
                            {(['table', 'graph', 'vendors'] as const).map(mode => (
                                <Button
                                    key={mode}
                                    variant={viewMode === mode ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => handleViewModeChange(mode)}
                                >
                                    {mode.charAt(0).toUpperCase() + mode.slice(1)}
                                </Button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {showingDetails && viewMode !== 'vendors' && (
                <LhcStatCards
                    stats={stats}
                    onStatCardClick={handleStatCardClick}
                    activeStat={activeStat}
                />
            )}

            {showingDetails && viewMode === 'vendors' && (
                <VendorStatCards
                    totalVendors={vendorTotalCount}
                    stats={vendorStats}
                    activeStat={activeVendorStat}
                    onStatCardClick={handleVendorStatCardClick}
                />
            )}

            <div>
                {viewMode === 'vendors' ? (
                    <LhcTable
                        data={vendorTableData}
                        loading={tableLoading || detailsLoading}
                        hasLoadedOnce={hasLoadedOnce}
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onSort={handleSort}
                        page={currentPage}
                        pageSize={rowsPerPage}
                        totalItems={totalRows}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={(size) => {
                            setRowsPerPage(size);
                            setCurrentPage(1);
                            lastSummaryPayloadRef.current = '';
                            lastDetailsPayloadRef.current = '';
                            lastVendorPayloadRef.current = '';
                        }}
                    />
                ) : viewMode === 'table' ? (
                    <LhcTable
                        data={tableData}
                        loading={tableLoading || detailsLoading}
                        hasLoadedOnce={hasLoadedOnce}
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onSort={handleSort}
                        onRowClick={showingDetails ? undefined : handleRowClick}
                        page={currentPage}
                        pageSize={rowsPerPage}
                        totalItems={totalRows}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={(size) => {
                            setRowsPerPage(size);
                            setCurrentPage(1);
                            lastSummaryPayloadRef.current = '';
                            lastDetailsPayloadRef.current = '';
                        }}
                    />
                ) : (
                    <LhcRouteGraph
                        data={tableData}
                        colorBy={colorBy}
                        colorByLabel={COLOR_BY_OPTIONS.find(o => o.value === colorBy)?.label ?? 'Category'}
                        loading={tableLoading || detailsLoading}
                    />
                )}
            </div>
        </div>
    );
};

export default LhcAnalysisPage;
